import { useId, type InputHTMLAttributes } from 'react'

export interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string
  error?: string | null
  hint?: string
}

/**
 * Labeled text input with error + hint wiring (label↔input association,
 * aria-invalid / aria-describedby on failure).
 */
export function Field({ label, error, hint, className = '', id, ...rest }: FieldProps) {
  const autoId = useId()
  const inputId = id ?? autoId
  const hintId = `${inputId}-hint`
  const errorId = `${inputId}-error`

  return (
    <div className={`flex flex-col gap-1.5 ${className}`}>
      <label htmlFor={inputId} className="text-small font-medium text-ink">
        {label}
      </label>
      <input
        id={inputId}
        className={`min-h-[44px] rounded-xl border bg-ground-2 px-4 text-body text-ink placeholder:text-ink-faint transition-colors duration-micro ease-porcelain focus:border-cobalt ${
          error ? 'border-terracotta' : 'border-line'
        }`}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : hint ? hintId : undefined}
        {...rest}
      />
      {hint && !error && (
        <p id={hintId} className="text-small text-ink-muted">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} className="text-small text-terracotta-deep" role="alert">
          {error}
        </p>
      )}
    </div>
  )
}
